import { type FieldValues, type Path, type UseFormSetError } from 'react-hook-form';

import { ApiRequestError } from '@/lib/api/client';

/**
 * docs/05-ADMIN-DASHBOARD.md §6.3 — "map 422 responses onto the offending
 * fields and scroll to the first one. Never show a raw JSON error."
 *
 * Used by every form in the dashboard, so the rule holds in one place rather
 * than being remembered thirty times.
 *
 * A message the form has nowhere to put — a field the server validated that
 * this screen does not render, or any non-422 failure — is returned instead of
 * being swallowed, so the caller can show it at form level.
 */
export interface ApplyApiErrorsResult {
  /** A message with nowhere to sit, for the caller to show at form level. */
  formError: string | null;
  /**
   * Whether anything landed on a field.
   *
   * Callers use it to decide against a generic fallback: the sign-in screen
   * showed "Something went wrong" *above* a field that already said the
   * credentials were wrong, which reads as two separate failures.
   */
  placed: boolean;
}

export function applyApiErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields: readonly Path<T>[],
): ApplyApiErrorsResult {
  if (!(error instanceof ApiRequestError)) {
    return { formError: null, placed: false };
  }

  if (error.status !== 422) {
    return { formError: error.payload.message, placed: false };
  }

  const known = new Set<string>(fields);
  const unplaceable: string[] = [];
  let first: Path<T> | null = null;

  for (const [field, messages] of Object.entries(error.validationErrors)) {
    const message = messages[0];

    if (message === undefined) {
      continue;
    }

    /*
     * Laravel names nested fields `blocks.0.html`. The form owns the whole
     * path when it renders an array, so it is matched as given — but a
     * `password_confirmation` rule reported against `password` still lands on
     * the field the person has to fix.
     */
    if (known.has(field)) {
      setError(field as Path<T>, { type: 'server', message });

      first ??= field as Path<T>;

      continue;
    }

    unplaceable.push(message);
  }

  if (first !== null) {
    focusField(String(first));
  }

  return {
    formError: unplaceable.length > 0 ? unplaceable.join(' ') : null,
    placed: first !== null,
  };
}

/**
 * `setFocus` from react-hook-form would be tidier, but it only reaches
 * registered inputs — and the first offending field is sometimes inside a
 * collapsed section or another locale's tab, where focusing silently fails.
 * Scrolling to the label is the part that has to work.
 */
function focusField(name: string): void {
  const element = document.querySelector<HTMLElement>(`[name="${CSS.escape(name)}"]`);

  if (element === null) {
    return;
  }

  element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  element.focus({ preventScroll: true });
}
